import { memo, useCallback, useMemo, useState } from 'react';
import { Button, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { SearchX } from 'lucide-react';

import { useReviewTable } from '../../../hooks';
import { IMPORT_ACTIONS } from '../../../constants';
import { ISSUE, ROW_STATUS } from '../../../constants/reviewFilters.js';
import { buildErrorsCsv } from '../../../utils/errorsCsv.js';
import { downloadTextFile } from '../../../utils/downloadTextFile.js';
import { buildManagerNames } from '../../../utils/managerNames.js';
import AssignAccessModal from './AssignAccessModal.jsx';
import FixRowDialog from './FixRowDialog.jsx';
import ReviewBulkBar from './ReviewBulkBar.jsx';
import ReviewSummaryLines from './ReviewSummaryLines.jsx';
import ReviewTable from './ReviewTable.jsx';
import ReviewToolbar from './ReviewToolbar.jsx';

const UNDO_DURATION_MS = 8000;
const MODAL = Object.freeze({ ASSIGN: 'assign' });

/**
 * Step 3: search the file, give access per person or in bulk, and fix errors without
 * uploading again. All state lives in the shared wizard reducer; this step only reads it and
 * dispatches. No invite action here: invitations are chosen in step 4.
 */
function ImportReviewStep() {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const review = useReviewTable();
  const { state, dispatch, selection, selectionState, visibleRows, pageRows } = review;
  const { catalog } = state;
  const managerNames = useMemo(() => buildManagerNames(state.rows, catalog.members), [state.rows, catalog.members]);
  const [modal, setModal] = useState(null);
  const [fixRowNumber, setFixRowNumber] = useState(null);

  const closeModal = useCallback(() => setModal(null), []);
  const closeFix = useCallback(() => setFixRowNumber(null), []);
  const openAssign = useCallback(() => setModal(MODAL.ASSIGN), []);
  const openEdit = useCallback((row) => setFixRowNumber(row.rowNumber), []);

  const changeAccess = useCallback(
    (rowNumber, access) =>
      dispatch({ type: IMPORT_ACTIONS.SET_ROW_ACCESS, payload: { rowNumbers: [rowNumber], access } }),
    [dispatch],
  );
  const confirmAssign = useCallback(
    (access) => {
      dispatch({ type: IMPORT_ACTIONS.SET_ROW_ACCESS, payload: { rowNumbers: selection, access } });
      setModal(null);
    },
    [dispatch, selection],
  );
  // No confirmation: the rows go at once and the toast offers Undo, as in the prototype.
  const removeWithUndo = useCallback(
    (rowNumbers) => {
    const removed = state.rows.filter((row) => rowNumbers.includes(row.rowNumber));
    dispatch({ type: IMPORT_ACTIONS.REMOVE_ROWS, payload: rowNumbers });
    toast.info({
      title: t('import.review.removed.title', { count: removed.length }),
      message: t('import.review.removed.message'),
      action: t('import.review.applied.undo'),
      duration: UNDO_DURATION_MS,
      onAction: () => dispatch({ type: IMPORT_ACTIONS.RESTORE_ROWS, payload: removed }),
    });
    },
    [dispatch, state.rows, toast, t],
  );
  const removeSelected = useCallback(() => removeWithUndo(selection), [removeWithUndo, selection]);
  const removeFixRow = useCallback(
    (rowNumber) => {
      removeWithUndo([rowNumber]);
      setFixRowNumber(null);
    },
    [removeWithUndo],
  );
  const saveFix = useCallback((changes) => dispatch({ type: IMPORT_ACTIONS.FIX_ROW, payload: changes }), [dispatch]);

  const quickFix = useCallback(
    (row, issue) => {
      if (issue.code === ISSUE.REPORTS_TO_SELF) {
        dispatch({ type: IMPORT_ACTIONS.FIX_ROW, payload: { rowNumber: row.rowNumber, person: { reports_to: '' } } });
        return;
      }
      const key = issue.code === ISSUE.UNKNOWN_ROLE ? 'roles' : 'groups';
      const names = row.accessFromFile[key].map((name) => (name === issue.value ? issue.suggestion : name));
      dispatch({
        type: IMPORT_ACTIONS.FIX_ROW,
        payload: { rowNumber: row.rowNumber, accessFromFile: { ...row.accessFromFile, [key]: names } },
      });
    },
    [dispatch],
  );

  const applyFromFile = useCallback(() => {
    // Members that already exist keep their access: they are neither counted nor snapshotted.
    const previous = state.rows
      .filter((row) => selection.includes(row.rowNumber) && row.status !== ROW_STATUS.UPDATE)
      .map((row) => ({ rowNumber: row.rowNumber, access: row.access }));
    dispatch({ type: IMPORT_ACTIONS.APPLY_ACCESS_FROM_FILE, payload: { rowNumbers: selection } });
    toast.info({
      title: t('import.review.applied.title'),
      message: t('import.review.applied.message', { count: previous.length }),
      action: t('import.review.applied.undo'),
      duration: UNDO_DURATION_MS,
      onAction: () => {
        dispatch({ type: IMPORT_ACTIONS.RESTORE_ROW_ACCESS, payload: previous });
        toast.success({
          title: t('import.review.applied.undoneTitle'),
          message: t('import.review.applied.undoneMessage'),
        });
      },
    });
  }, [dispatch, state.rows, selection, toast, t]);

  const downloadErrors = useCallback(() => {
    const csv = buildErrorsCsv(
      state.rows,
      state.file.headers,
      (issue) => t(`import.review.issues.${issue.code}`, { value: issue.value }),
      [t('import.review.errorsCsv.row'), t('import.review.errorsCsv.problems')],
    );
    downloadTextFile(t('import.review.errorsCsv.fileName'), csv);
  }, [state.rows, state.file.headers, t]);

  const tableSelection = useMemo(
    () => ({
      enabled: true,
      isRowSelected: review.isRowSelected,
      onToggleRow: review.toggleRow,
      allSelected: selectionState.allOnPage,
      onToggleAll: review.togglePage,
      selectAllAriaLabel: t('import.review.table.selectAll'),
      rowSelectAriaLabel: (row) => t('import.review.table.selectRow', { row: row.rowNumber }),
    }),
    [review.isRowSelected, review.toggleRow, review.togglePage, selectionState.allOnPage, t],
  );

  const fixRow = state.rows.find((row) => row.rowNumber === fixRowNumber);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <ReviewToolbar
        search={review.search}
        filters={review.filters}
        fileRoles={review.fileRoles}
        counters={review.counters}
        onSearch={review.setSearch}
        onFilters={review.setFilters}
        onClearAll={review.clearAll}
        onDownloadErrors={downloadErrors}
        hasErrors={review.counters.error > 0}
      />
      <ReviewSummaryLines summary={review.summary} onShowThem={review.showNewWithoutAccess} />
      {selection.length > 0 ? (
        <ReviewBulkBar
          selectedCount={selection.length}
          pageCount={pageRows.length}
          totalCount={visibleRows.length}
          onClear={review.clearSelection}
          onSelectAll={review.selectAllRows}
          onAssign={openAssign}
          onApplyFromFile={applyFromFile}
          onRemove={removeSelected}
        />
      ) : null}
      {visibleRows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10">
          <StatusMessage
            icon={SearchX}
            title={t('import.review.table.emptyTitle')}
            subtitle={t('import.review.table.emptySubtitle')}
          />
          <Button variant="secondary" size="sm" onClick={review.clearAll}>
            {t('import.review.clearFilters')}
          </Button>
        </div>
      ) : (
        <ReviewTable
          rows={pageRows}
          catalog={catalog}
          managerNames={managerNames}
          pagination={review.pagination}
          selection={tableSelection}
          onAccessChange={changeAccess}
          onEdit={openEdit}
          onQuickFix={quickFix}
          onPrevPage={review.prevPage}
          onNextPage={review.nextPage}
          onPageSizeChange={review.changePageSize}
        />
      )}
      {modal === MODAL.ASSIGN ? (
        <AssignAccessModal
          count={selection.length}
          roles={catalog.roles}
          groups={catalog.groups}
          onConfirm={confirmAssign}
          onClose={closeModal}
        />
      ) : null}
      {fixRow ? <FixRowDialog row={fixRow} onSave={saveFix} onRemove={removeFixRow} onClose={closeFix} /> : null}
    </div>
  );
}

export default memo(ImportReviewStep);
